import { mdiCalendarBlank, mdiClockOutline } from '@mdi/js';

import { ROUNDED_CONTROL_RADIUS } from 'components/theme/controlRadius';

/**
 * Corner of the dialog's paper: a step rounder than the controls inside it
 * (ROUNDED_CONTROL_RADIUS, 12), so that a rounded field or button near the
 * edge sits concentric with the paper rather than sharper than it.
 */
export const REDESIGN_DIALOG_RADIUS = 16;

/** The paper's maximum width per `size`; it is fullWidth up to that. */
export const REDESIGN_DIALOG_SIZES = { sm: 480, md: 720, lg: 1040, xl: 1360 };

/** The default width of a split dialog's main (fields) pane. */
export const REDESIGN_DIALOG_SPLIT_MAIN_WIDTH = 440;

/**
 * Keyboard focus in the dialog: the redesigns' accent ring, outside the
 * control so that it never covers its edge or its text.
 * @param {{accent: string}} colors getRedesignDialogColors(theme)
 * @returns {Object} JSS style
 */
export const getRedesignDialogFocusRing = (colors) => ({
  outline: `2px solid ${colors.accent}`,
  outlineOffset: 2,
});

/**
 * A 24 x 24 mdi glyph as a CSS url() of an SVG data URI, for a mask.
 * @param {string} path the glyph's path data
 * @returns {string}
 */
const glyphUrl = (path) =>
  `url("data:image/svg+xml,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="${path}"/></svg>`
  )}")`;

const CALENDAR_GLYPH = glyphUrl(mdiCalendarBlank);
const CLOCK_GLYPH = glyphUrl(mdiClockOutline);

const CALENDAR_TYPES = ['date', 'datetime-local', 'month', 'week'];
const CLOCK_TYPES = ['time'];
const INDICATOR = '::-webkit-calendar-picker-indicator';

/**
 * The selector of the button Chrome draws in the native date and time
 * inputs, for the given input types, with `state` (a pseudo-class of the
 * input) and `after` (a pseudo-class of the button).
 * @param {string[]} types
 * @param {{state?: string, after?: string}} [options]
 * @returns {string}
 */
const pickerIndicator = (types, { state = '', after = '' } = {}) =>
  types
    .map((type) => `& input[type="${type}"]${state}${INDICATOR}${after}`)
    .join(', ');

/**
 * The native picker button repainted in `color`: Chrome paints its glyph
 * black whatever the theme, which the dark surfaces do not show. The button
 * keeps its box, size and position; the background image goes and a fill of
 * the colour shows through a mask of the same glyph, laid out as Chrome lays
 * its image (inside the padding, contained, centred). The expense tracking
 * redesign does the same on its page (ExpenseTrackingRedesignStyles).
 * @param {string} glyph the glyph, a CSS url()
 * @param {string} color
 * @returns {Object} JSS style
 */
const pickerIndicatorPaint = (glyph, color) => ({
  backgroundImage: 'none',
  backgroundColor: color,
  WebkitMaskImage: glyph,
  maskImage: glyph,
  WebkitMaskRepeat: 'no-repeat',
  maskRepeat: 'no-repeat',
  WebkitMaskPosition: 'center',
  maskPosition: 'center',
  WebkitMaskSize: 'contain',
  maskSize: 'contain',
  WebkitMaskOrigin: 'content-box',
  maskOrigin: 'content-box',
  cursor: 'pointer',
});

const OUTLINE = '.MuiOutlinedInput-notchedOutline';

/** Where an outlined field's text starts (MUI's OutlinedInput input padding). */
export const OUTLINED_TEXT_INSET = 14;

/**
 * MUI's padding of an outlined Autocomplete's box (its inputRoot): 9px, 6px
 * when dense (TextInput, size="small"). Its first input adds 6px more, so its
 * text stood at 15px, 12px dense, beside the other fields' 14px.
 */
const AUTOCOMPLETE_ROOT_PADDING = 9;
const AUTOCOMPLETE_ROOT_PADDING_DENSE = 6;
const AUTOCOMPLETE_ROOT = '.MuiAutocomplete-inputRoot.MuiOutlinedInput-root';
const AUTOCOMPLETE_FIRST_INPUT = '.MuiAutocomplete-input:first-child';

const ADORNMENT_BUTTON = '.MuiInputAdornment-root .MuiIconButton-root';
const AUTOCOMPLETE_BUTTONS = [
  '.MuiAutocomplete-popupIndicator',
  '.MuiAutocomplete-clearIndicator',
];
const FIELD_BUTTONS = [ADORNMENT_BUTTON, ...AUTOCOMPLETE_BUTTONS];

/** `& a, & b` for the given selectors, each with `suffix`. */
const each = (selectors, suffix = '') =>
  selectors.map((selector) => `& ${selector}${suffix}`).join(', ');

/**
 * The house fields' look inside the dialog, as rules for its paper (nest
 * them under '&&', so that each selector starts with two classes). They
 * reach every outlined field in the paper — TextInput, TextInput `select`,
 * Autocomplete inputs (their text at the other fields' 14px inset),
 * DateTimePicker's native date and time inputs, MUI TextField
 * variant="outlined" — and the checkboxes, radios, switches, dividers and
 * progress indicators.
 *
 * Every selector outranks the MUI and house rule it overrides whatever the
 * order of the sheets (a sheet created later wins at equal specificity, and
 * a lazily loaded scene can create MUI's after this one): the paper's two
 * classes come first, and the hover rule repeats the `:not()` chain of the
 * themes' own MuiOutlinedInput override. Portaled parts — the Select menu,
 * the Autocomplete popper, tooltips — render outside the paper and keep the
 * theme's look.
 * @param {ReturnType<import('./redesignDialogColors').getRedesignDialogColors>} colors
 * @returns {Object} JSS style
 */
export const getRedesignDialogFieldRules = (colors) => ({
  // Outlined fields: TextInput's `rounded` corner (the outline inherits
  // it), the dialog's text; an outline for each state.
  '& .MuiOutlinedInput-root': {
    borderRadius: ROUNDED_CONTROL_RADIUS,
  },
  '& .MuiInputBase-root': {
    color: colors.text,
  },
  '& .MuiInputBase-root.Mui-disabled': {
    color: colors.disabled,
  },
  [`& ${OUTLINE}`]: {
    borderColor: colors.fieldOutline,
  },
  [`& .MuiOutlinedInput-root:hover:not(.Mui-disabled):not(.Mui-focused):not(.Mui-error) ${OUTLINE}`]:
    {
      borderColor: colors.fieldOutlineHover,
    },
  [`& .MuiOutlinedInput-root.Mui-focused ${OUTLINE}`]: {
    borderColor: colors.accent,
    borderWidth: 2,
  },
  [`& .MuiOutlinedInput-root.Mui-error ${OUTLINE}`]: {
    borderColor: colors.danger,
  },
  [`& .MuiOutlinedInput-root.Mui-disabled ${OUTLINE}`]: {
    borderColor: colors.fieldOutlineDisabled,
  },
  // Chrome's autofill paints the theme's blue (#266798) behind the text.
  '& .MuiInputBase-input:-webkit-autofill': {
    WebkitBoxShadow: `0 0 0 100px ${colors.surface} inset`,
    WebkitTextFillColor: colors.text,
    caretColor: colors.text,
  },
  // A focused select's fill follows the field's corner (MUI squares it).
  '& .MuiSelect-select:focus': {
    backgroundColor: colors.hoverFill,
    borderRadius: 'inherit',
  },
  // An Autocomplete's text (the ComboBox's value) 14px into its box like the
  // other fields' (a column of fields keeps one edge): the first input's left
  // padding only, so the box, its height, the label and the open and clear
  // buttons stay MUI's. An input after tags (multiple) keeps its padding.
  [`& ${AUTOCOMPLETE_ROOT} ${AUTOCOMPLETE_FIRST_INPUT}`]: {
    paddingLeft: OUTLINED_TEXT_INSET - AUTOCOMPLETE_ROOT_PADDING,
  },
  [`& ${AUTOCOMPLETE_ROOT}.MuiOutlinedInput-marginDense ${AUTOCOMPLETE_FIRST_INPUT}`]:
    {
      paddingLeft: OUTLINED_TEXT_INSET - AUTOCOMPLETE_ROOT_PADDING_DENSE,
    },

  // Labels, helper and error texts (MUI's order: an error wins).
  '& .MuiFormLabel-root': {
    color: colors.muted,
  },
  '& .MuiFormLabel-root.Mui-focused': {
    color: colors.accent,
  },
  '& .MuiFormLabel-root.Mui-disabled': {
    color: colors.disabled,
  },
  '& .MuiFormLabel-root.Mui-error, & .MuiFormLabel-asterisk.Mui-error': {
    color: colors.danger,
  },
  '& .MuiFormHelperText-root': {
    color: colors.muted,
  },
  '& .MuiFormHelperText-root.Mui-disabled': {
    color: colors.disabled,
  },
  '& .MuiFormHelperText-root.Mui-error': {
    color: colors.danger,
  },

  // Icons in and next to a field: the select arrow, the adornments (text
  // and buttons), the Autocomplete's open and clear buttons. MUI paints them
  // in the theme's action colour, white in all three themes.
  '& .MuiInputAdornment-root, & .MuiInputAdornment-root .MuiTypography-colorTextSecondary':
    {
      color: colors.muted,
    },
  [`& .MuiSelect-icon, ${each(FIELD_BUTTONS)}`]: {
    color: colors.muted,
  },
  [`& .MuiSelect-icon.Mui-disabled, ${each(FIELD_BUTTONS, '.Mui-disabled')}`]: {
    color: colors.disabled,
  },
  '@media (hover: hover)': {
    [each(FIELD_BUTTONS, ':hover')]: {
      backgroundColor: colors.hoverFill,
      color: colors.text,
    },
    '& .MuiCheckbox-root:hover, & .MuiRadio-root:hover, & .MuiSwitch-switchBase:hover':
      {
        backgroundColor: colors.hoverFill,
      },
  },

  // The native date and time buttons, muted like the select arrow.
  [pickerIndicator(CALENDAR_TYPES)]: pickerIndicatorPaint(
    CALENDAR_GLYPH,
    colors.muted
  ),
  [pickerIndicator(CLOCK_TYPES)]: pickerIndicatorPaint(
    CLOCK_GLYPH,
    colors.muted
  ),
  [pickerIndicator([...CALENDAR_TYPES, ...CLOCK_TYPES], {
    state: ':not(:disabled)',
    after: ':hover',
  })]: {
    backgroundColor: colors.text,
  },
  [pickerIndicator([...CALENDAR_TYPES, ...CLOCK_TYPES], {
    state: ':disabled',
  })]: {
    backgroundColor: colors.disabled,
    cursor: 'default',
  },

  // Checkboxes and radios: muted, the accent when checked.
  '& .MuiCheckbox-root, & .MuiRadio-root': {
    color: colors.muted,
  },
  '& .MuiCheckbox-root.Mui-checked, & .MuiCheckbox-root.MuiCheckbox-indeterminate, & .MuiRadio-root.Mui-checked':
    {
      color: colors.accent,
    },
  '& .MuiCheckbox-root.Mui-disabled, & .MuiRadio-root.Mui-disabled': {
    color: colors.disabled,
  },

  // Switches: a muted thumb on a muted track, both in the accent when on
  // (the themes paint #ccc on white, and a green track).
  '& .MuiSwitch-switchBase': {
    color: colors.thumb,
  },
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: colors.accent,
  },
  '& .MuiSwitch-switchBase.Mui-disabled': {
    color: colors.thumb,
  },
  '& .MuiSwitch-track': {
    backgroundColor: colors.muted,
    opacity: 0.5,
  },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundColor: colors.accent,
    opacity: 0.5,
  },
  '& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': {
    backgroundColor: colors.muted,
    opacity: 0.2,
  },

  // Rules and progress: the theme's divider is white, its primary navy.
  '& .MuiDivider-root': {
    backgroundColor: colors.border,
  },
  '& .MuiCircularProgress-colorPrimary': {
    color: colors.accent,
  },
  '& .MuiLinearProgress-colorPrimary': {
    backgroundColor: colors.selectedFill,
  },
  '& .MuiLinearProgress-barColorPrimary': {
    backgroundColor: colors.accent,
  },
});
